import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { GlobalContext } from '../../../context/GlobalContext';
import useApi from '../../../hooks/useApi';
import haxios from '../../../lib/haxios';
import ReportLayout from '../core/ReportLayout';
import ReportKPICards from '../core/ReportKPICards';
import DataTable from '../shared/DataTable';
import { Plus, X, Loader2, Briefcase } from 'lucide-react';

const BDMReport = () => {
  const { uinfo } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [visitNotes, setVisitNotes] = useState([]);
  const [allBDMData, setAllBDMData] = useState([]);
  const [filters, setFilters] = useState({});
  const [showAddVisitNoteModal, setShowAddVisitNoteModal] = useState(false);
  const [newVisitNote, setNewVisitNote] = useState({
    account: '',
    customerName: '',
    visitDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
    noteText: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [selectedRepFilter, setSelectedRepFilter] = useState(null);

  // Get BDM activity data - using visit notes API (same as dashboard green table)
  const { data: salesOperators, isLoading: operatorsLoading } = useApi({ 
    url: '/salesassist/cplusassist/getOperators', 
    initial: [] 
  });

  // Fetch visit notes (same API and logic as dashboard green table)
  const fetchVisitNotes = async () => {
    try {
      setLoading(true);
      console.log('BDMReport - Fetching visit notes (same as dashboard green table)');
      
      // Fetch visit notes (same API as dashboard green table)
      const visitNotesParams = {};
      if (uinfo?.Status !== 'admin') {
        visitNotesParams.rep = uinfo?.Inits;
      }
      
      const visitNotesResponse = await haxios.get('/visiondashboard/visitnotes', { params: visitNotesParams });
      const notes = visitNotesResponse.data || [];
      
      console.log('BDMReport - Visit notes count:', notes.length);
      setVisitNotes(notes);
      
      // Also get aggregated data for KPIs
      if (notes && notes.length > 0) {
        // Get today's performance data for each rep
        const todayResponse = await haxios.get('/salesassist/cplusassist/salesMarginByBranchAndInitsToday');
        const todayData = todayResponse.data || [];

        // Extract unique reps from visit notes (created_by field)
        const uniqueReps = new Set();
        notes.forEach(note => {
          if (note.created_by) {
            uniqueReps.add(note.created_by);
          }
        });

        // Count visit notes per rep
        const visitNotesByRep = {};
        notes.forEach(note => {
          const rep = note.created_by;
          if (rep) {
            if (!visitNotesByRep[rep]) {
              visitNotesByRep[rep] = [];
            }
            visitNotesByRep[rep].push(note);
          }
        });

        // Process aggregated BDM data for KPIs
        const bdmData = Array.from(uniqueReps).map(repInits => {
          const operator = salesOperators?.find(op => 
            op.Inits === repInits || 
            op.inits === repInits ||
            op.Name === repInits ||
            op.name === repInits
          );

          const todayPerformance = todayData.find(item => 
            item.inits === repInits ||
            item.inits === operator?.Inits
          ) || {};

          const repVisitNotes = visitNotesByRep[repInits] || [];
          const visitCount = repVisitNotes.length;

          const salesValue = todayPerformance.invoiceValueToday || 
                            todayPerformance.totalSales || 
                            todayPerformance.salesToday || 
                            todayPerformance.sales || 0;

          const invoiceCount = todayPerformance.invoicesToday || 
                              todayPerformance.invoices || 
                              todayPerformance.invoiceCount || 
                              0;

          const marginValue = todayPerformance.totalMargin || 
                            todayPerformance.marginToday || 
                            todayPerformance.margin || 
                            todayPerformance.marginValue || 0;

          const marginPercent = salesValue && salesValue > 0 ? 
                              ((marginValue / salesValue) * 100) : 
                              (todayPerformance.marginPercentToday || 
                               todayPerformance.marginPercent || 
                               0);

          return {
            bdm: repInits || operator?.Inits || 'N/A',
            name: operator?.Name || operator?.name || operator?.OperatorName || repInits || 'Unknown',
            branch: operator?.Branch || operator?.branch || todayPerformance.branch || 'Unknown',
            todaySales: parseFloat(salesValue) || 0,
            todayInvoices: parseInt(invoiceCount) || 0,
            todayMargin: parseFloat(marginValue) || 0,
            todayMarginPercent: parseFloat(marginPercent) || 0,
            visitNotes: visitCount,
            activityScore: calculateActivityScore({ sales: salesValue, invoices: invoiceCount }, visitCount)
          };
        });

        setAllBDMData(bdmData);
      }
    } catch (error) {
      console.error('BDMReport - Error fetching visit notes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate activity score
  const calculateActivityScore = (performance, visitCount) => {
    const salesScore = (parseFloat(performance.sales) || 0) / 1000; // Normalize sales
    const invoiceScore = (parseInt(performance.invoices) || 0) * 10; // Weight invoices
    const visitScore = (parseInt(visitCount) || 0) * 5; // Weight visits
    
    return Math.round(salesScore + invoiceScore + visitScore);
  };

  // Read rep filter from URL query parameter on mount
  useEffect(() => {
    const repParam = searchParams.get('rep');
    if (repParam) {
      const decodedRep = decodeURIComponent(repParam);
      setSelectedRepFilter(decodedRep);
      console.log('BDMReport - Rep filter from URL:', decodedRep);
    } else {
      setSelectedRepFilter(null);
    }
  }, [searchParams]);

  // Fetch data when dependencies are loaded
  useEffect(() => {
    fetchVisitNotes();
  }, [salesOperators, uinfo?.Status, uinfo?.Inits]);

  // Process data for KPI cards - focus on BDM-relevant metrics
  const processedData = useMemo(() => {
    // Filter visit notes by rep if specified from query parameter
    let filteredVisitNotes = visitNotes;
    if (selectedRepFilter && visitNotes && visitNotes.length > 0) {
      filteredVisitNotes = visitNotes.filter(note => {
        const createdBy = (note.created_by || '').toString().toUpperCase();
        const filterRep = selectedRepFilter.toUpperCase();
        // Match by created_by field (rep initials/name)
        return createdBy === filterRep || 
               createdBy.includes(filterRep) || 
               filterRep.includes(createdBy);
      });
      console.log('BDMReport - Filtered to rep:', selectedRepFilter, 'Visit notes count:', filteredVisitNotes.length);
    }

    if (!filteredVisitNotes || filteredVisitNotes.length === 0) {
      return {
        kpis: [],
        tableData: []
      };
    }

    // Calculate BDM-specific metrics from filtered visit notes
    const totalVisits = filteredVisitNotes.length;
    
    // Count unique customers visited
    const uniqueCustomers = new Set();
    filteredVisitNotes.forEach(note => {
      if (note.account) {
        uniqueCustomers.add(note.account);
      }
    });
    const uniqueCustomersCount = uniqueCustomers.size;
    
    // Calculate average visits per customer
    const avgVisitsPerCustomer = uniqueCustomersCount > 0 ? totalVisits / uniqueCustomersCount : 0;
    
    // Get sales data for visited customers (from allBDMData aggregated)
    const totalSalesFromVisits = allBDMData.reduce((sum, bdm) => sum + (bdm.todaySales || 0), 0);
    
    // Get total sales target if available (from today's performance data)
    const totalSalesTarget = allBDMData.reduce((sum, bdm) => {
      // Try to get target from operator data
      const operator = salesOperators?.find(op => op.Inits === bdm.bdm || op.inits === bdm.bdm);
      const target = operator?.SalesTarget || 2000; // Default target
      return sum + target;
    }, 0);
    
    const salesTargetProgress = totalSalesTarget > 0 ? (totalSalesFromVisits / totalSalesTarget) * 100 : 0;

    // Create KPI cards - 4 focused BDM metrics
    const kpis = [
      {
        key: 'total-visits',
        label: 'Total Visit Notes',
        value: totalVisits,
        type: 'bdms',
        format: 'number'
      },
      {
        key: 'unique-customers',
        label: 'Unique Customers Visited',
        value: uniqueCustomersCount,
        type: 'customers',
        format: 'number'
      },
      {
        key: 'avg-visits-per-customer',
        label: 'Avg Visits per Customer',
        value: avgVisitsPerCustomer,
        type: 'bdms',
        format: 'decimal'
      },
      {
        key: 'sales-from-visits',
        label: 'Sales from Visited Customers',
        value: totalSalesFromVisits,
        type: 'sales',
        format: 'currency'
      }
    ];

    // Create table data - show filtered visit notes (same structure as dashboard green table)
    const tableData = filteredVisitNotes.map(note => ({
      account: note.account || 'N/A',
      customerName: note.customer_name || 'N/A',
      createdBy: note.created_by || 'N/A',
      visitDate: note.visit_datetime ? new Date(note.visit_datetime) : null,
      noteText: note.note_text || '',
      noteId: note.note_id || Math.random()
    }));

    return { kpis, tableData };
  }, [allBDMData, visitNotes, selectedRepFilter]);

  // Table columns - same structure as dashboard green table
  const columns = [
    {
      key: 'account',
      label: 'Account',
      sortable: true
    },
    {
      key: 'customerName',
      label: 'Name',
      sortable: true
    },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: true
    },
    {
      key: 'visitDate',
      label: 'Visit Date',
      sortable: true,
      render: (value) => {
        if (!value) return 'N/A';
        try {
          return format(new Date(value), 'dd/MM/yy HH:mm');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      key: 'noteText',
      label: 'Note',
      sortable: false,
      render: (value) => (
        <div className="truncate max-w-xs" title={value}>
          {value || 'N/A'}
        </div>
      )
    }
  ];

  // Filter options
  const filterOptions = [
    {
      key: 'bdm',
      label: 'BDM',
      type: 'select',
      options: salesOperators?.map(op => ({
        value: op.inits || op.name,
        label: `${op.inits || op.name} - ${op.name || op.inits}`
      })) || []
    },
    {
      key: 'activity',
      label: 'Activity Level',
      type: 'select',
      options: [
        { value: 'high', label: 'High Activity (100+)' },
        { value: 'medium', label: 'Medium Activity (50-99)' },
        { value: 'low', label: 'Low Activity (<50)' }
      ]
    }
  ];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearAll = () => {
    setFilters({});
  };

  const handleClearRepFilter = () => {
    setSelectedRepFilter(null);
    setSearchParams({});
    navigate('/reports/bdm');
  };

  const handleApplyFilters = () => {
    console.log('BDMReport - Applying filters:', filters);
    // TODO: Implement filter logic
  };

  const handleExportPDF = () => {
    console.log('BDMReport - Exporting to PDF');
    // TODO: Implement PDF export
  };

  const handleExportExcel = () => {
    console.log('BDMReport - Exporting to Excel');
    // TODO: Implement Excel export
  };

  const handleEmailReport = () => {
    console.log('BDMReport - Emailing report');
    // TODO: Implement email functionality
  };

  const handleAddVisitNote = async () => {
    if (!newVisitNote.account || !newVisitNote.noteText.trim()) {
      alert('Please enter both account and note text');
      return;
    }

    try {
      setIsCreating(true);
      
      // First, check if we need to create a visit first or can add note directly
      // Based on RepVisits.js, notes are added to visits, so we may need to create a visit first
      // For now, let's try creating a visit note with the account and note text
      const visitData = {
        rep_code: uinfo?.Inits || uinfo?.Name,
        visit_datetime: newVisitNote.visitDate,
        customer_account: newVisitNote.account,
        notes: newVisitNote.noteText
      };

      // Create visit first
      const visitResponse = await haxios.post('/visiondashboard/repvisits', visitData);
      
      if (visitResponse?.success && visitResponse?.visit_id) {
        // If visit was created successfully, the note should be included
        // Refresh the visit notes list
        await fetchVisitNotes();
        setNewVisitNote({
          account: '',
          customerName: '',
          visitDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
          noteText: ''
        });
        setShowAddVisitNoteModal(false);
      } else {
        throw new Error('Failed to create visit');
      }
    } catch (error) {
      console.error('Failed to add visit note:', error);
      alert('Failed to add visit note. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <ReportLayout
      title="BDM Activity Report"
      subtitle="Daily activity tracking and performance metrics for Business Development Managers"
      icon={Briefcase}
      iconColor="green"
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      onEmailReport={handleEmailReport}
      loading={loading || operatorsLoading}
      rightActions={
        selectedRepFilter ? (
          <button
            onClick={handleClearRepFilter}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear Rep Filter
          </button>
        ) : null
      }
    >
      <div className="px-6">
        {/* Rep Filter Indicator */}
        {selectedRepFilter && (
          <div className="mb-4 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-indigo-800">
                Showing data for rep: <strong>{selectedRepFilter}</strong>
                {processedData.tableData.length === 0 && (
                  <span className="ml-2 text-indigo-600">(No matching visit notes found)</span>
                )}
              </span>
              <button
                onClick={handleClearRepFilter}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline"
              >
                Show all visit notes
              </button>
            </div>
          </div>
        )}
        {/* KPI Cards */}
        <ReportKPICards kpis={processedData.kpis} loading={loading} />

        {/* Visit Notes Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900">Visit Notes</h3>
            </div>
            <button
              onClick={() => setShowAddVisitNoteModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Visit Note</span>
            </button>
          </div>
          <DataTable
            data={processedData.tableData}
            columns={columns}
            loading={loading || operatorsLoading}
            searchable={true}
            sortable={true}
            pagination={true}
            pageSize={20}
            emptyMessage="No visit notes available"
            searchPlaceholder="Search visit notes..."
          />
        </div>
      </div>

      {/* Add Visit Note Modal */}
      {showAddVisitNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Add Visit Note</h2>
              <button
                onClick={() => {
                  setShowAddVisitNoteModal(false);
                  setNewVisitNote({
                    account: '',
                    customerName: '',
                    visitDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
                    noteText: ''
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number *
                </label>
                <input
                  type="text"
                  value={newVisitNote.account}
                  onChange={(e) => setNewVisitNote({ ...newVisitNote, account: e.target.value })}
                  placeholder="Enter account number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isCreating}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={newVisitNote.customerName}
                  onChange={(e) => setNewVisitNote({ ...newVisitNote, customerName: e.target.value })}
                  placeholder="Enter customer name (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isCreating}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={newVisitNote.visitDate}
                  onChange={(e) => setNewVisitNote({ ...newVisitNote, visitDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isCreating}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note Text *
                </label>
                <textarea
                  value={newVisitNote.noteText}
                  onChange={(e) => setNewVisitNote({ ...newVisitNote, noteText: e.target.value })}
                  placeholder="Enter visit note..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isCreating}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddVisitNoteModal(false);
                    setNewVisitNote({
                      account: '',
                      customerName: '',
                      visitDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
                      noteText: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddVisitNote}
                  disabled={!newVisitNote.account || !newVisitNote.noteText.trim() || isCreating}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Add Visit Note</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ReportLayout>
  );
};

export default BDMReport;
